// CS_50137_AUDIT_LOG_PAGE off only: the Audit log export dialog. Delete with the flag.
import React, { useCallback, useMemo, useState } from "react";
import ChooseOrganization from "components/OrgFilter/ChooseOrganization";
import { Button, Label, Modal, ModalHeader, ModalBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useAuthorizationContext } from "contexts/AuthorizationContext";
import ListDropdown from "components/ListDropdown";
import TimeRangePicker from "components/TimeRangePicker";
import HierarchicalSelector from "components/HierarchicalSelector";
import "./style.css";
import { Store as notifyStore } from "react-notifications-component";
import { NotifyContent, tempOptions } from "components/Notify";
import utils from "utils";
import PropTypes from "prop-types";

import downloadAuditLogExport from "./downloadAuditLogExport";
import useAuditLogScope, { describeHierarchySelection } from "./useAuditLogScope";

const formatDateForExport = (date) => {
  if (!date) return undefined;
  const iso = new Date(date).toISOString();
  return `${iso.slice(0, 19)}Z`;
};

const AuditLog = ({ isOpen = false, toggle = () => {} }) => {
  const { t } = useTranslation("common");
  const { user, groups, isOrgFilterEnabled } = useAuthorizationContext();
  const {
    usableOrganizations,
    activeOrganizationGuid,
    mustChooseOrganization,
    chooseOrganization,
    reset: resetScope,
    selectedSiteGuids,
    selectedGatewayIdentifiers,
    selectedControllerGuids,
    selectedUserIds,
    setSelectedUserIds,
    organizationUsers,
    isLoadingOrganizationUsers,
    siteByGuid,
    gatewayByIdentifier,
    controllerGuidToGatewayIdentifier,
    selectedOrganization,
    selectedOrganizationGuid,
    organizationSelectionLocked,
    handleHierarchySelectionChange,
    selectedIdsByType,
    hierarchyLabels,
    visibleHierarchyData,
  } = useAuditLogScope({ isActive: isOpen });

  const [dateFrom, setDateFrom] = useState();
  const [dateTo, setDateTo] = useState();
  const [selectedRangeId, setSelectedRangeId] = useState("today");
  const [timezone, setTimezone] = useState(
    () => utils.getIanaTimezone?.() || "UTC"
  );
  const [isExporting, setIsExporting] = useState(false);
  const [timeRangePickerKey, setTimeRangePickerKey] = useState(0);

  const handleModalToggle = () => {
    resetScope();
    setDateFrom();
    setDateTo();
    setSelectedRangeId("today");
    setTimezone(utils.getIanaTimezone?.() || "UTC");
    setIsExporting(false);
    setTimeRangePickerKey((prev) => prev >= 1 ? 0 : 1);
    toggle();
  };

  const hierarchySummaryFormatter = useCallback(
    (selectedMap = {}) => {
      if (selectedOrganization?.name) {
        return selectedOrganization.name;
      }

      return describeHierarchySelection(t, selectedMap) ?? t("auditLog.placeholder");
    },
    [organizationSelectionLocked, selectedOrganization, t]
  );

  const handleUserToggle = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleExportClick = async (event) => {
    event.preventDefault();

    if (!selectedOrganizationGuid) {
      utils.debug(
        "AuditLog export: missing selected organization",
        utils.MSG_TYPE_ERR
      );
      return;
    }

    handleModalToggle();
    notifyStore.addNotification({
      ...tempOptions,
      content: NotifyContent("success", t("report.longProcessWarningBackground")),
    });

    const resolveSiteGroupId = (site) =>
      groups.find((g) => g.guid === site?.siteGuid)?.id ?? null;

    const siteEntriesByGuid = new Map();
    const addSiteEntry = (siteGuid, candidateSite) => {
      if (!siteGuid || siteEntriesByGuid.has(siteGuid)) return;
      const site = siteByGuid.get(siteGuid) || candidateSite;
      const siteGroupId = resolveSiteGroupId(site);
      siteEntriesByGuid.set(siteGuid, {
        siteGuid,
        siteGroupId,
      });
    };

    for (const siteGuid of selectedSiteGuids) {
      addSiteEntry(siteGuid);
    }

    const gatewayExportIdentifiers = [];
    const seenGatewayIdentifiers = new Set();
    const addGatewayIdentifier = (gatewayIdentifier) => {
      if (!gatewayIdentifier || seenGatewayIdentifiers.has(gatewayIdentifier))
        return;
      seenGatewayIdentifiers.add(gatewayIdentifier);
      gatewayExportIdentifiers.push(gatewayIdentifier);
    };

    for (const gatewayIdentifier of selectedGatewayIdentifiers) {
      addGatewayIdentifier(gatewayIdentifier);
      const gateway = gatewayByIdentifier.get(gatewayIdentifier);
      const siteGuid = gateway?.site?.siteGuid || gateway?.siteGuid;
      addSiteEntry(siteGuid, gateway?.site);
    }

    const controllerExportGuids = [];
    const seenControllerGuids = new Set();

    for (const controllerGuid of selectedControllerGuids) {
      if (!controllerGuid || seenControllerGuids.has(controllerGuid)) continue;
      seenControllerGuids.add(controllerGuid);
      controllerExportGuids.push(controllerGuid);

      const gatewayIdentifier =
        controllerGuidToGatewayIdentifier.get(controllerGuid);
      addGatewayIdentifier(gatewayIdentifier);
      const gateway = gatewayByIdentifier.get(gatewayIdentifier);
      const siteGuid = gateway?.site?.siteGuid || gateway?.siteGuid;
      addSiteEntry(siteGuid, gateway?.site);
    }

    const exportSites = Array.from(siteEntriesByGuid.values());
    const exportParams = {
      organization: {
        organizationGuid: selectedOrganizationGuid,
        organizationGroupId: selectedOrganization.id,
      },
      sites: exportSites,
      gateways: gatewayExportIdentifiers,
      controllers: controllerExportGuids,
      userIamIds: selectedUserIds,
      from: formatDateForExport(dateFrom),
      to: formatDateForExport(dateTo),
      timezone: timezone || utils.getIanaTimezone?.() || "UTC",
      language: user().language,
    };

    setIsExporting(true);
    try {
      await downloadAuditLogExport(exportParams, t);
    } finally {
      setIsExporting(false);
    }
  };

  const getUserDropdownTitle = () => {
    if (isLoadingOrganizationUsers) return t("auditLog.loadingUsers");
    if (organizationUsers.length === 0) return t("auditLog.noUsersAvailable");
    if (selectedUserIds.length === 0) {
      return `${t("auditLog.users")} (${t("auditLog.all").toString().toLowerCase()})`;
    }
    return `${t("auditLog.users")} (${selectedUserIds.length} ${t("auditLog.selected")})`;
  };

  const userDropdownTitle = getUserDropdownTitle();

  const userItems = useMemo(() => {
    if (isLoadingOrganizationUsers) {
      return [
        {
          id: "audit-log-users-loading",
          text: t("auditLog.loadingUsers"),
          disabled: true,
        },
      ];
    }

    if (organizationUsers.length === 0) {
      return [
        {
          id: "audit-log-users-empty",
          text: t("auditLog.noUsersAvailable"),
          disabled: true,
        },
      ];
    }

    return organizationUsers
      .slice()
      .sort((a, b) => {
        const nameA = a.username?.trim().toLowerCase() || (a.email ?? "");
        const nameB = b.username?.trim().toLowerCase() || (b.email ?? "");
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      })
      .map((candidate) => {
        const id = candidate.userId ?? candidate.id;
        const primaryText = candidate.username?.trim() || candidate.email || "";
        const email = candidate.email?.trim();
        return {
          id,
          text: primaryText,
          description: email && email !== primaryText ? email : undefined,
          checkbox: true,
          checkboxStatus: selectedUserIds.includes(id),
          command: () => handleUserToggle(id),
        };
      });
  }, [selectedUserIds, isLoadingOrganizationUsers, organizationUsers, t]);

  const closeLabel = t("generic.close");
  const closeButton = (
    <button
      type="button"
      className="close"
      onClick={handleModalToggle}
      aria-label={closeLabel}
    >
      <span aria-hidden="true">{"\u00d7"}</span>
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleModalToggle}
      size="lg"
      centered
      className="audit-log-modal"
    >
      <ModalHeader toggle={handleModalToggle} close={closeButton}>
        {t("auditLog.userAuditLog")}
      </ModalHeader>
      <ModalBody>
        {isOrgFilterEnabled && (
          <ChooseOrganization
            inputId="audit-log-choose-organization"
            organizations={usableOrganizations}
            onChoose={chooseOrganization}
            value={activeOrganizationGuid ?? ""}
          />
        )}
        {!mustChooseOrganization && (
        <>
        <div className="mb-4 audit-log-section">
          <div>
            <Label>{t("auditLog.organizationHierarchyLabel")}</Label>
            <HierarchicalSelector
              className="audit-log-dropdown"
              data={visibleHierarchyData}
              selectedIdsByType={selectedIdsByType}
              labels={hierarchyLabels}
              summaryFormatter={hierarchySummaryFormatter}
              onSelectionChange={handleHierarchySelectionChange}
              selectDescendants={true}
              isLoading={false}
              disabled={visibleHierarchyData.length === 0}
              t={t}
            />
          </div>
          <div>
            <Label>{t("auditLog.users")}</Label>
            <ListDropdown
              className="audit-log-dropdown"
              title={userDropdownTitle}
              listItems={userItems}
              disabled={
                isLoadingOrganizationUsers || organizationUsers.length === 0
              }
            />
          </div>
        </div>
        <TimeRangePicker
          key={`trp-${timeRangePickerKey}`}
          t={t}
          defaultRangeId={selectedRangeId}
          defaultFrom={dateFrom}
          defaultTo={dateTo}
          onChange={({ from, to, rangeId, timezone: nextTimezone }) => {
            setDateFrom(from);
            setDateTo(to);
            setSelectedRangeId(rangeId);
            if (nextTimezone) {
              setTimezone(nextTimezone);
            }
          }}
          hideTime={true}
          hideTimezone={true}
        />
        <div className="float-right mt-4">
          <Button
            color="primary"
            onClick={handleExportClick}
            disabled={isExporting || !selectedOrganizationGuid}
          >
            {t("auditLog.exportAuditLog")}
          </Button>
        </div>
        </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default AuditLog;

AuditLog.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
};
